import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Skeleton, Typography } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'

import { DataTableHeaderSortingSingle } from './data-table-header/data-table-header-sorting-single'

import type { DataTableModel } from '../atoms'
import type { Column } from './data-table-types'
import type { TableFilter } from '../atoms/table-filter'
import type { ClassNameProps } from '@root/shared/react'

type DataTableHeaderProps<
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
> = {
  column: Column<D, ColumnId, Context>
  model: DataTableModel<D, ColumnId, object, Context>
  context: Context
  showInactiveSorting?: boolean
} & ClassNameProps

export const DataTableHeaderCell = <
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
>({
  className,
  column,
  model,
  context,
  showInactiveSorting = false,
}: DataTableHeaderProps<D, ColumnId, Context>) => {
  type ResolvedColumn = Column<D, ColumnId, Context>
  const { t } = useTranslation()
  const [sorting] = useAtom(model.sorting)
  const [filters] = useAtom(model.filters)

  const setColumnFilterAction = useAction(model.setColumnFilter)
  const clearColumnFilterAction = useAction(model.clearColumnFilter)

  const toggleSorting = useAction(model.toggleSorting)

  const handleClick = (column: ResolvedColumn) => {
    if (column.sortable) {
      toggleSorting(column.id)
    }
  }

  const currentSorting = (() => {
    if (!column.sortable) {
      return undefined
    }

    const currentSorting =
      sorting?.sortColumn === column.id ? sorting.sortDirection : undefined

    return currentSorting
  })()

  if (column.hidden) {
    return undefined
  }

  // Let's keep commented for now, it doesn't make sense to hide header while loading
  // if (AsyncStateN.isLoading(itemsState)) {
  //   return <HeaderCellSkeleton width={30} height={12} />
  // }

  const filter = filters[column.id]
  const setColumnFilter = (filter: TableFilter<ColumnId>) =>
    setColumnFilterAction(column.id, filter)

  const clearColumnFilter = () => clearColumnFilterAction(column.id)

  const style: React.CSSProperties = {
    minWidth: column.minWidth,
    maxWidth: column.maxWidth,
    justifyContent: column.justify,
  }

  const header =
    typeof column.header === 'function'
      ? column.header({
          t,
          filter,
          setColumnFilter,
          clearColumnFilter,
          columnId: column.id,
          itemsStateAtom: model,
          style: style,
          context: context,
        })
      : column.header

  const renderSortingIcon = () => {
    if (!currentSorting) {
      if (showInactiveSorting) {
        return <SortingIcon sorting={'asc'} active={false} />
      }
      return undefined
    }

    return <SortingIcon sorting={currentSorting} active={true} />
  }

  return (
    <HeaderCell
      className={className}
      onClick={() => handleClick(column)}
      key={column.id}
      style={style}
      data-clickable={column.sortable ?? false}
      data-has-sorting={Boolean(currentSorting)}
    >
      <Label component="div" ellipsis>
        {header}
      </Label>
      {renderSortingIcon()}
    </HeaderCell>
  )
}

const HeaderCell = styled.div`
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  gap: 1px;
  color: var(--data-table-header-color, var(--text-8));
  transition: color 0.2s ease-in-out;
  justify-content: start;
  white-space: nowrap;

  &[data-has-sorting='true'] {
    color: var(--data-table-header-color-hover, var(--text-12));
  }

  &[data-clickable='true'] {
    cursor: pointer;
    &:hover {
      color: var(--data-table-header-color-hover, var(--text-12));
    }
  }
`

const Label = styled(Typography)`
  line-height: 16px;
  display: flex;
`

const _HeaderCellSkeleton = styled(Skeleton)`
  margin-block-start: 9px;
`

const SortingIcon = styled(DataTableHeaderSortingSingle)``
